import { createSelector } from '@ngrx/store';

import { Scopes } from '@ninety/platform/models/entitlements/entitlements-model';

import { FeatureFlagKeys } from '../../app-entities/feature-flag/feature-flag-state.model';
import { selectFeatureFlag } from '../../app-entities/feature-flag/feature-flag-state.selectors';
import { selectCurrentUser } from '../../app-entities/users/users-state.selectors';
import { selectCompany } from '../company/company-state.selectors';
import { selectCurrentPerson, selectCurrentPersonsEmail } from '../current-person/current-person.selectors';
import { selectFeatureEnabled } from '../entitlements/entitlements-state.selectors';
import { IntercomStateKey, selectGlobalAppState } from '../index';

export const selectFinJwtValidation = selectFeatureFlag(FeatureFlagKeys.finJwtValidation);

/** The Maz copilot (MazSelectors.selectIsCopilotEnabled) has "Talk to a person", so Intercom's launcher steps aside. */
export const selectMazCopilotReplacesLauncher = createSelector(
  selectFeatureFlag(FeatureFlagKeys.webMazRedesign),
  selectFeatureEnabled(Scopes.mazConversationalData),
  (redesign, canAccessMaz) => !!redesign && canAccessMaz
);

export const selectIntercomState = createSelector(selectGlobalAppState, globalState => globalState?.[IntercomStateKey]);

/** Script loaded and, when Fin JWT validation is on, the JWT handed to Intercom. */
export const selectIntercomReadyForConversation = createSelector(
  selectIntercomState,
  selectFinJwtValidation,
  (state, finJwtValidation) => state?.loadState === 'loaded' && (!!state.jwtApplied || !finJwtValidation)
);

/** Open by any path (launcher, contact support, a feedback click). */
export const selectIntercomMessengerOpen = createSelector(selectIntercomState, state => !!state?.messengerOpen);

/** What a held Provide Feedback click should do now; null keeps holding. */
export const selectFeedbackOutcome = createSelector(
  selectIntercomReadyForConversation,
  selectIntercomState,
  (ready, state): 'ready' | 'failed' | null => {
    if (ready) return 'ready';
    return state?.loadState === 'failed' ? 'failed' : null;
  }
);

export const selectIntercomProps = createSelector(
  selectCurrentUser,
  selectCompany,
  selectCurrentPerson,
  selectCurrentPersonsEmail,
  (user, company, person, email): IntercomRecord => {
    if (!user || !company || !person || !email) return null;

    return {
      user_id: user._id,
      email: email,
      name: user.fullName,
      company: {
        id: company._id,
        name: company.name,
      },
    };
  }
);
export type IntercomValue = boolean | string | number | IntercomRecord;
export type IntercomRecord = { [key: string]: IntercomValue };
